import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RocketLaunchIcon, FolderOpenIcon } from '@heroicons/react/24/outline';
import { companyApi } from '../../services/api';
import ConfirmDialog from '../ConfirmDialog';

const CTASection: React.FC = () => {
  const navigate = useNavigate();
  const [hasCompany, setHasCompany] = useState<boolean | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // 检查用户是否有主体
  useEffect(() => {
    const checkCompany = async () => {
      try {
        const response = await companyApi.getCompanies();
        setHasCompany(response.data && response.data.length > 0);
      } catch (e) {
        setHasCompany(false);
      }
    };
    checkCompany();
    
    // 监听主体更新事件，刷新主体状态
    const handleCompanyUpdate = () => {
      checkCompany();
    };
    window.addEventListener('company:updated', handleCompanyUpdate);
    
    return () => {
      window.removeEventListener('company:updated', handleCompanyUpdate);
    };
  }, []);

  return (
    <section className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div
          className="relative rounded-3xl p-14 md:p-20 text-center overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 50%, #0369a1 100%)' }}
        >
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className="absolute rounded-full"
              style={{
                top: '-60px', right: '-60px',
                width: '300px', height: '300px',
                background: 'radial-gradient(circle, rgba(96,165,250,0.25) 0%, transparent 70%)',
              }}
            />
            <div
              className="absolute rounded-full"
              style={{
                bottom: '-60px', left: '-40px',
                width: '250px', height: '250px',
                background: 'radial-gradient(circle, rgba(34,211,238,0.2) 0%, transparent 70%)',
              }}
            />
            <div
              className="absolute inset-0 opacity-[0.04]"
              style={{
                backgroundImage: `
                  linear-gradient(to right, white 1px, transparent 1px),
                  linear-gradient(to bottom, white 1px, transparent 1px)
                `,
                backgroundSize: '40px 40px',
              }}
            />
          </div>

          <div className="relative z-10">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight tracking-tight">
              立即开始，高效创建，专业标书
            </h2>
            <p className="text-xl text-blue-200 mb-12 max-w-xl mx-auto leading-relaxed">
              无需复杂配置，AI 全程辅助，快速产出高质量标书
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4">
              <button
                onClick={() => {
                  if (hasCompany === false) {
                    setShowConfirm(true);
                  } else {
                    navigate('/create');
                  }
                }}
                className="inline-flex items-center gap-2.5 px-8 py-4 bg-white text-blue-700 font-bold text-base rounded-2xl hover:bg-blue-50 transition-all duration-200 shadow-xl hover:scale-[1.02]"
              >
                <RocketLaunchIcon className="w-5 h-5" />
                立即新建标书
              </button>
              <button
                onClick={() => navigate('/projects')}
                className="inline-flex items-center gap-2.5 px-8 py-4 text-white font-semibold text-base rounded-2xl border-2 border-white/30 hover:border-white/60 hover:bg-white/10 transition-all duration-200"
              >
                <FolderOpenIcon className="w-5 h-5" />
                查看我的项目
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 主体缺失确认弹框 */}
      <ConfirmDialog
        isOpen={showConfirm}
        title="需要设置投标主体"
        message="创建标书需要先设置投标主体信息。您当前还没有主体信息，建议先创建主体后再进行标书创建。"
        confirmText="去创建主体"
        cancelText="去创建标书"
        confirmButtonClass="bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
        onConfirm={() => {
          setShowConfirm(false);
          navigate('/companies/new');
        }}
        onCancel={() => {
          setShowConfirm(false);
          navigate('/create');
        }}
      />
    </section>
  );
};

export default CTASection;
